import { useState } from "react";
import { useCart } from "../lib/cart";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import { toast } from "sonner";
import { X, Plus, Minus, Trash2 } from "lucide-react";

export default function CartDrawer() {
  const { items, removeItem, updateQty, subtotal, open, setOpen, count } =
    useCart();
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    if (!items.length) return;
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id,
          size: i.size,
          quantity: i.quantity,
        })),
        origin_url: window.location.origin,
        currency: "cad",
      };
      const { data } = await api.post("/checkout/session", payload);
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="cart-drawer"
      className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm flex justify-end"
      onClick={() => setOpen(false)}
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-zinc-950 border-l border-zinc-800 h-full flex flex-col fade-up"
      >
        <header className="flex items-center justify-between px-6 py-5 border-b border-zinc-900">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-green-400">
              {t("cart")}
            </p>
            <h2 className="font-display text-2xl uppercase tracking-tighter">
              {count} {count === 1 ? "item" : "items"}
            </h2>
          </div>
          <button
            onClick={() => setOpen(false)}
            data-testid="cart-close"
            className="text-zinc-400 hover:text-white"
            aria-label="close"
          >
            <X size={22} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {items.length === 0 && (
            <p className="text-zinc-500 text-sm text-center py-16 uppercase tracking-widest">
              Empty cart
            </p>
          )}
          {items.map((i) => (
            <div
              key={i.key}
              data-testid={`cart-item-${i.product_id}`}
              className="flex gap-4 py-4 border-b border-zinc-900"
            >
              <img
                src={i.image_url}
                alt=""
                className="w-20 h-24 object-cover bg-black shrink-0"
              />
              <div className="flex-1 min-w-0">
                <h4 className="font-display uppercase text-sm tracking-tight truncate">
                  {i.name}
                </h4>
                <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                  Size: {i.size}
                </p>
                <p className="text-green-400 font-display text-lg mt-1">
                  ${i.price.toFixed(0)}
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <button
                    onClick={() => updateQty(i.key, i.quantity - 1)}
                    className="border border-zinc-800 hover:border-green-400 p-1"
                    data-testid={`cart-dec-${i.product_id}`}
                  >
                    <Minus size={12} />
                  </button>
                  <span className="px-2 text-sm">{i.quantity}</span>
                  <button
                    onClick={() => updateQty(i.key, i.quantity + 1)}
                    className="border border-zinc-800 hover:border-green-400 p-1"
                    data-testid={`cart-inc-${i.product_id}`}
                  >
                    <Plus size={12} />
                  </button>
                  <button
                    onClick={() => removeItem(i.key)}
                    className="border border-zinc-800 hover:border-red-500 hover:text-red-500 p-1 ml-auto"
                    data-testid={`cart-remove-${i.product_id}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <footer className="px-6 py-5 border-t border-zinc-900 space-y-3">
          <div className="flex justify-between text-sm text-zinc-400 uppercase tracking-widest">
            <span>Subtotal</span>
            <span>${subtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-xs text-zinc-500 uppercase tracking-widest">
            <span>Shipping</span>
            <span>$15.00 CAD</span>
          </div>
          <div className="flex justify-between text-lg uppercase tracking-widest font-bold border-t border-zinc-900 pt-3">
            <span>Total</span>
            <span className="text-green-400 font-display">
              ${(subtotal + (items.length ? 15 : 0)).toFixed(2)} CAD
            </span>
          </div>
          <button
            onClick={checkout}
            disabled={!items.length || loading}
            data-testid="cart-checkout-btn"
            className="w-full bg-green-500 hover:bg-green-400 text-black font-bold uppercase tracking-widest py-4 disabled:opacity-40 transition-colors"
          >
            {loading ? "Loading…" : "Checkout"}
          </button>
        </footer>
      </aside>
    </div>
  );
}
